import React, { useState, useEffect, useMemo } from 'react'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Plus, Edit, Trash2, Building, Mail, Phone, MapPin, Eye } from 'lucide-react'
import { useClients, Client } from '@/hooks/useClients'
import { supabase } from '@/integrations/supabase/client'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { smartleadAPI } from '@/lib/smartlead-api'


interface ClientFormData {
  name: string
  domain: string
}

export function ClientsManagement() {
  const { data: allClients = [], refetch } = useClients()
  const { isAdmin, profile } = useAuth()
  // Filter out the system "default" client used for global API keys
  const clients = allClients.filter(client => client.id !== 'default')
  
  // State for filtering and deleted clients
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'active' | 'churned' | 'deleted'>('all')
  const [deletedClients, setDeletedClients] = useState<Client[]>([])
  const [deletedClientsLoading, setDeletedClientsLoading] = useState(false)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [editingClient, setEditingClient] = useState<Client | null>(null)
  const [showDialog, setShowDialog] = useState(false)

  // Fetch deleted clients when component mounts
  useEffect(() => {
    const fetchDeletedClients = async () => {
      if (!isAdmin) return
      
      // Check for demo mode first
      if (!smartleadAPI.isConfigured()) {
        console.log('🔑 [ClientsManagement] Smartlead API not configured, returning 0 deleted clients for demo mode...')
        setDeletedClients([]) // Demo mode: no deleted clients
        setDeletedClientsLoading(false)
        return
      }
      
      setDeletedClientsLoading(true)
      try {
        const { data, error } = await supabase
          .from('clients')
          .select('id, name, status, updated_at, notes')
          .eq('status', 'deleted')
          .neq('id', 'default')
          .order('updated_at', { ascending: false })

        if (error) throw error
        setDeletedClients(data || [])
      } catch (error) {
        console.error('Error fetching deleted clients:', error)
      } finally {
        setDeletedClientsLoading(false)
      }
    }

    fetchDeletedClients()
  }, [isAdmin])

  // Memoize filtered clients
  const filteredClients = useMemo(() => {
    if (selectedFilter === 'deleted') {
      return deletedClients
    }
    
    switch (selectedFilter) {
      case 'active':
        return clients.filter(c => c.status === 'Active')
      case 'churned':
        return clients.filter(c => c.status === 'Inactive' || c.status === 'Churned')
      case 'all':
      default:
        return clients
    }
  }, [selectedFilter, clients, deletedClients])

  // Memoize stats calculations
  const activeClientsCount = useMemo(() => 
    clients.filter(c => c.status === 'Active').length,
    [clients]
  )

  const churnedClientsCount = useMemo(() => 
    clients.filter(c => c.status === 'Inactive' || c.status === 'Churned').length,
    [clients]
  )
  
  const [formData, setFormData] = useState<ClientFormData>({
    name: '',
    domain: ''
  })

  const resetForm = () => {
    setFormData({
      name: '',
      domain: ''
    })
    setEditingClient(null)
  }

  const handleInputChange = (field: keyof ClientFormData, value: string | number | string[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleEdit = (client: Client) => {
    setEditingClient(client)
    setFormData({
      name: client.name,
      domain: client.domain || ''
    })
    setShowDialog(true)
  }

  const handleViewDetails = (client: Client) => {
    navigate(`/clients/${client.id}`)
  }


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.name.trim()) {
      toast.error('Client name is required')
      return
    }

    setIsLoading(true)
    try {
      if (editingClient) {
        // Update existing client
        const { error } = await supabase
          .from('clients')
          .update({
            name: formData.name,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingClient.id)

        if (error) throw error
        toast.success('Client updated successfully')
      } else {
        // Create new client - Generate unique client_id with retry logic
        let numericClientId: number;
        let attempts = 0;
        const maxAttempts = 10;
        let isUnique = false;
        
        while (!isUnique && attempts < maxAttempts) {
          attempts++;
          
          // Get max existing client_id from database (use maybeSingle to handle empty table)
          const { data: maxClientData, error: maxError } = await supabase
            .from('clients')
            .select('client_id')
            .order('client_id', { ascending: false })
            .limit(1)
            .maybeSingle()
          
          const maxClientId = maxClientData?.client_id || 0;
          console.log(`[ClientsManagement] Max existing client_id: ${maxClientId}, attempt ${attempts}`)
          
          // Generate new ID: max + 1, or use timestamp-based if max is very high
          if (maxClientId < 999999) {
            numericClientId = maxClientId + 1;
          } else {
            // If max is too high, use timestamp-based with milliseconds for better uniqueness
            const timestamp = Date.now();
            numericClientId = parseInt(timestamp.toString().slice(-6)) + Math.floor(Math.random() * 1000);
          }
          
          // Double-check this ID doesn't exist (race condition protection)
          const { data: existingClient, error: checkError } = await supabase
            .from('clients')
            .select('client_id')
            .eq('client_id', numericClientId)
            .maybeSingle()
          
          // If maybeSingle returns null/undefined and no error (or "not found" error), ID is unique
          if (!existingClient) {
            if (!checkError || checkError.code === 'PGRST116') {
              // No row found - ID is unique!
              isUnique = true;
              console.log(`[ClientsManagement] Generated unique client_id: ${numericClientId}`)
              break;
            } else {
              // Real error occurred
              console.warn('[ClientsManagement] Error checking client_id uniqueness:', checkError)
            }
          } else {
            // Client exists with this ID - try again
            console.log(`[ClientsManagement] client_id ${numericClientId} already exists, trying again...`)
          }
          
          // If we've tried many times, use a more unique timestamp-based approach
          if (attempts >= maxAttempts - 1) {
            // Final fallback: use full timestamp with random component
            const timestamp = Date.now();
            numericClientId = parseInt(timestamp.toString().slice(-9)) + Math.floor(Math.random() * 10000);
            console.log(`[ClientsManagement] Using final fallback client_id: ${numericClientId}`)
            // Don't check uniqueness for fallback - just try it
            isUnique = true;
            break;
          }
        }
        
        if (!isUnique) {
          throw new Error('Failed to generate unique client_id after multiple attempts')
        }
        
        let createdClient = null;
        const newClientData = {
          id: crypto.randomUUID(), // Generate UUID for id field (required)
          name: formData.name,
          client_id: numericClientId,
          domain: formData.domain || null
        }
        
        console.log('[ClientsManagement] Creating new client with data:', newClientData)
        console.log('[ClientsManagement] Generated unique client_id:', numericClientId)
        
        const { data, error } = await supabase
          .from('clients')
          .insert(newClientData)
          .select()
          .single()
        
        if (error) {
          console.error('Supabase insert error:', error)
          console.error('Error details:', JSON.stringify(error, null, 2))
          
          // If it's a duplicate key error, try one more time with a different ID
          if (error.code === '23505' && (error.message?.includes('client_id') || error.message?.includes('clients_client_id_unique'))) {
            console.log('[ClientsManagement] Duplicate client_id detected, retrying with new ID...')
            console.log('[ClientsManagement] Attempted client_id:', numericClientId)
            
            // Generate a completely new ID using timestamp + milliseconds + random
            const timestamp = Date.now();
            const fallbackClientId = parseInt(timestamp.toString().slice(-9)) + Math.floor(Math.random() * 10000);
            
            console.log('[ClientsManagement] Retrying with fallback client_id:', fallbackClientId)
            
            const retryClientData = {
              ...newClientData,
              client_id: fallbackClientId
            }
            
            const { data: retryData, error: retryError } = await supabase
              .from('clients')
              .insert(retryClientData)
              .select()
              .single()
            
            if (retryError) {
              console.error('[ClientsManagement] Retry also failed:', retryError)
              console.error('[ClientsManagement] Retry error details:', JSON.stringify(retryError, null, 2))
              
              // If retry also fails with duplicate, try one more time with even more randomness
              if (retryError.code === '23505' && (retryError.message?.includes('client_id') || retryError.message?.includes('clients_client_id_unique'))) {
                console.log('[ClientsManagement] Second retry also had duplicate, trying one more time...')
                const finalClientId = parseInt(Date.now().toString()) + Math.floor(Math.random() * 100000);
                const finalRetryData = {
                  ...newClientData,
                  client_id: finalClientId
                }
                
                const { data: finalData, error: finalError } = await supabase
                  .from('clients')
                  .insert(finalRetryData)
                  .select()
                  .single()
                
                if (finalError) {
                  console.error('[ClientsManagement] Final retry also failed:', finalError)
                  throw finalError
                }
                
                createdClient = finalData
                console.log('[ClientsManagement] Successfully created client with final fallback client_id:', finalClientId)
              } else {
                throw retryError
              }
            } else {
              createdClient = retryData
              console.log('[ClientsManagement] Successfully created client with fallback client_id:', fallbackClientId)
            }
          } else {
            throw error
          }
        } else {
          createdClient = data
        }
        
        if (!createdClient) {
          throw new Error('Failed to create client - no data returned')
        }
        
        toast.success('Client created successfully')
      }

      refetch()
      setShowDialog(false)
      resetForm()
    } catch (error) {
      console.error('Error saving client:', error)
      toast.error('Failed to save client')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async (clientId: string, clientName: string) => {
    // Check if user is admin
    if (!isAdmin) {
      toast.error('Only administrators can delete clients')
      return
    }

    if (!confirm(`Are you sure you want to delete client "${clientName}"? This action cannot be undone.`)) {
      return
    }

    console.log('Starting delete for client:', clientId, clientName, 'by admin:', profile?.full_name)
    console.log('🔍 Client ID type and value:', typeof clientId, clientId)
    setIsLoading(true)
    try {
      // Update the client record to mark it as deleted and track who deleted it
      console.log('🔍 Marking client as deleted:', clientName, 'by:', profile?.full_name)
      
      const { error: updateError } = await supabase
        .from('clients')
        .update({
          status: 'deleted',
          notes: `DELETED by ${profile?.full_name || 'Unknown'} on ${new Date().toLocaleString()}`
        })
        .eq('id', clientId)

      if (updateError) {
        console.error('❌ Failed to mark client as deleted:', updateError)
        throw updateError
      }

      console.log('✅ Client marked as deleted successfully')

      console.log('Delete successful, invalidating cache...')
      
      // Remove the specific client from cache immediately
      queryClient.setQueryData(['clients'], (oldData: Client[] | undefined) => {
        if (!oldData) return []
        console.log('Old data:', oldData)
        const newData = oldData.filter(client => client.id !== clientId)
        console.log('New data after filter:', newData)
        return newData
      })
      
      // Also invalidate to ensure fresh fetch
      await queryClient.invalidateQueries({ queryKey: ['clients'] })
      
      toast.success(`Client "${clientName}" deleted successfully by ${profile?.full_name || 'admin'}`)
      console.log('Cache updated successfully')
    } catch (error) {
      console.error('Error deleting client:', error)
      toast.error('Failed to delete client')
    } finally {
      setIsLoading(false)
    }
  }

  const getStatusBadge = (status: string) => {
    const statusStyles = {
      'Active': 'bg-green-100 text-green-800 border-green-200',
      'Inactive': 'bg-muted text-muted-foreground border-border',
      'Paused': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Prospect': 'bg-blue-100 text-blue-800 border-blue-200'
    }
    return (
      <Badge className={statusStyles[status as keyof typeof statusStyles] || statusStyles.Active}>
        {status}
      </Badge>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Clients</h1>
          <p className="text-muted-foreground text-sm">
            Manage your client information and relationships
          </p>
        </div>
        
        <Dialog open={showDialog} onOpenChange={(open) => {
          setShowDialog(open)
          if (!open) resetForm()
        }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Add Client
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingClient ? 'Edit Client' : 'Add New Client'}
              </DialogTitle>
              <DialogDescription>
                {editingClient ? 'Update client information' : 'Enter client details to add them to your system'}
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Client Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="John Smith"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="domain">Domain</Label>
                  <Input
                    id="domain"
                    value={formData.domain}
                    onChange={(e) => handleInputChange('domain', e.target.value)}
                    placeholder="example.com"
                  />
                </div>

              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowDialog(false)}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {editingClient ? 'Update Client' : 'Add Client'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className={`grid grid-cols-1 gap-4 ${isAdmin ? 'md:grid-cols-3' : 'md:grid-cols-3'}`}>
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${
            selectedFilter === 'active' ? 'ring-2 ring-green-500 bg-green-50' : ''
          }`}
          onClick={() => setSelectedFilter('active')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active clients</CardTitle>
            <Building className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {activeClientsCount}
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${
            selectedFilter === 'churned' ? 'ring-2 ring-red-500 bg-red-50' : ''
          }`}
          onClick={() => setSelectedFilter('churned')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Churned clients</CardTitle>
            <Building className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {churnedClientsCount}
            </div>
          </CardContent>
        </Card>

        {/* Deleted Clients Card - Admin Only */}
        {isAdmin && (
          <Card 
            className={`cursor-pointer transition-all hover:shadow-md ${
              selectedFilter === 'deleted' ? 'ring-2 ring-orange-500 bg-orange-50' : ''
            }`}
            onClick={() => setSelectedFilter('deleted')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Deleted clients</CardTitle>
              <Trash2 className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {deletedClientsLoading ? '...' : deletedClients.length}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Clients Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            {selectedFilter === 'deleted' ? 'Deleted Clients' : 'Clients List'}
          </CardTitle>
          <CardDescription>
            {selectedFilter === 'deleted' 
              ? 'View clients that have been deleted and who deleted them'
              : 'View and manage all your clients'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredClients.length === 0 ? (
            <div className="text-center py-12">
              <Building className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-2 text-sm font-semibold text-foreground">
                {selectedFilter === 'deleted' ? 'No deleted clients' : 'No clients'}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {selectedFilter === 'deleted' 
                  ? 'No clients have been deleted yet.'
                  : 'Get started by adding your first client.'
                }
              </p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client Name</TableHead>
                      {selectedFilter === 'deleted' ? (
                        <>
                          <TableHead>Deleted By</TableHead>
                          <TableHead>Deleted At</TableHead>
                        </>
                      ) : (
                        <>
                          <TableHead>Contact</TableHead>
                          <TableHead>Status</TableHead>
                        </>
                      )}
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                <TableBody>
                  {filteredClients.map((client) => (
                    <TableRow 
                      key={client.id} 
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleViewDetails(client)}
                    >
                      <TableCell className="font-medium">{client.name}</TableCell>
                      {selectedFilter === 'deleted' ? (
                        <>
                          <TableCell>
                            <div className="text-sm">
                              {client.notes ? (
                                client.notes.includes('DELETED by') ? (
                                  <span className="text-red-700">
                                    {client.notes.split('DELETED by ')[1]?.split(' on ')[0] || 'Unknown'}
                                  </span>
                                ) : 'Unknown'
                              ) : 'Unknown'}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm text-muted-foreground">
                              {client.notes ? (
                                client.notes.includes(' on ') ? (
                                  client.notes.split(' on ')[1] || 'Unknown time'
                                ) : 'Unknown time'
                              ) : 'Unknown time'}
                            </div>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>
                            <div className="space-y-1">
                              {(client.main_contact_email || client.email) && (
                                <div className="flex items-center gap-1 text-sm">
                                  <Mail className="h-3 w-3" />
                                  {client.main_contact_email || client.email}
                                </div>
                              )}
                              {client.main_account_manager && (
                                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                  <span className="text-xs">{client.main_account_manager}</span>
                                </div>
                              )}
                              {client.phone && (
                                <div className="flex items-center gap-1 text-sm">
                                  <Phone className="h-3 w-3" />
                                  {client.phone}
                                </div>
                              )}
                              {!client.main_contact_email && !client.email && !client.main_account_manager && !client.phone && '-'}
                            </div>
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(client.status || 'Active')}
                          </TableCell>
                        </>
                      )}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {selectedFilter === 'deleted' ? (
                            <Badge variant="destructive">Deleted</Badge>
                          ) : (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleViewDetails(client)
                                }}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleEdit(client)
                                }}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              {isAdmin && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleDelete(client.id, client.name)
                                  }}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  title="Delete client (Admin only)"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  )
}